import {
    AnalyticsProvider,
    PageTracker,
    PostHogAnalyticsProvider,
    SentryErrorTracker,
} from '@order-up/analytics';
import { clientApiFeatureFlagsProvider } from '@order-up/client/vite';
import { useState, type ReactNode } from 'react';

const APP_NAME = 'onboarding';

/**
 * Module-level guard so `SentryErrorTracker.initializeWithModule(...)` runs
 * exactly once per browser tab regardless of how many `<AnalyticsBoundary>`
 * instances mount (Strict Mode double-invokes effects in dev). Sentry's own
 * `init()` is idempotent at the SDK level, but we keep our own guard so we
 * never load `@sentry/react` twice on a hot reload.
 */
let sentryTracker: SentryErrorTracker | null = null;

function getOrCreateSentryTracker(): SentryErrorTracker {
    if (sentryTracker) return sentryTracker;
    // Inject the DSN/env/release from `import.meta.env.VITE_*`. `process.env`
    // does NOT exist in a Vite client bundle, so without this injection the
    // tracker's DSN is permanently empty and `isConfigured()` stays false —
    // making `captureException` a silent no-op. See AUTH_BOOT_DIAGNOSTIC /
    // Initiative #3 PR 8 for the original (broken) Vite path.
    const tracker = new SentryErrorTracker(APP_NAME, {
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment:
            import.meta.env.VITE_VERCEL_ENV
            || import.meta.env.VITE_APP_ENV
            || import.meta.env.MODE,
        release: import.meta.env.VITE_SENTRY_RELEASE,
    });
    if (typeof window !== 'undefined') {
        void import('@sentry/react').then((Sentry) => {
            // `main.tsx` already calls `Sentry.init(...)` directly at module
            // load. `initializeWithModule` detects the live client via
            // `getClient()` and attaches WITHOUT re-initializing.
            tracker.initializeWithModule(Sentry);
        });
    }
    sentryTracker = tracker;
    return tracker;
}

/**
 * Vite-side analytics boundary for the onboarding app. Mirrors the Next-side
 * `apps/management/src/components/AnalyticsBoundary.tsx` shape. Phase 6
 * Wave 2.
 */
export function AnalyticsBoundary({ children }: { children: ReactNode }) {
    const [analyticsProvider] = useState(
        () =>
            new PostHogAnalyticsProvider(APP_NAME, {
                apiKey: import.meta.env.VITE_POSTHOG_API_KEY,
                host: import.meta.env.VITE_POSTHOG_HOST,
            }),
    );
    const [errorTracker] = useState(() => getOrCreateSentryTracker());

    return (
        <AnalyticsProvider
            provider={analyticsProvider}
            featureFlags={clientApiFeatureFlagsProvider}
            errorTracker={errorTracker}
        >
            <PageTracker />
            {children}
        </AnalyticsProvider>
    );
}
